import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Button from '@mulesoft/anypoint-components/lib/Button';
import { getPath } from '~/utils/routes';
import {
  dateFormat,
  scannerPropType,
  scannerRunPropType,
  FORM_ASSET_TYPE_LABELS
} from '~/components/AgentScanners/types/commonTypes';
import LoadingElement from '~/components/Layout/LoadingElement';
import FormattedDate from '~/components/Layout/FormattedDate';
import { getScanErrorMessage } from '~/components/AgentScanners/utils/utils';
import ScanFailureBanner from '../ScanFailureBanner';
import Overview from './Overview';
import Review from './Review';
import styles from './ScanAuditLog.css';

const ACTIONABLE_STATUSES = new Set([
  'NEW',
  'PENDING_UPDATE',
  'PENDING_DELETE'
]);

const isBackendIgnored = (agent) =>
  String(agent.ignored).toLowerCase() === 'true';

const isActionable = (agent) =>
  ACTIONABLE_STATUSES.has(agent.stagingStatus) && !isBackendIgnored(agent);

const ScanAuditLog = ({
  scanner,
  scannerId,
  scanAuditLogId,
  organizationId,
  scannerRun,
  isFetchingScannerRuns,
  stagingAgents,
  isFetchingStagingAgents,
  loadScanners,
  loadScannerRuns,
  loadStagingAgents,
  setAlert,
  publishAgents
}) => {
  const history = useHistory();
  const isReviewMode = scannerRun?.status === 'PENDING_REVIEW';
  const isStale = scannerRun?.status === 'STALE';

  const [selectedAgentIds, setSelectedAgentIds] = React.useState(
    () => new Set()
  );

  const [unIgnoredIds, setUnIgnoredIds] = React.useState(() => new Set());
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [isBannerDismissed, setIsBannerDismissed] = React.useState(false);

  // Only seed the selection once per scan run — re-fetches of `stagingAgents`
  // (e.g. polling) must not discard the user's manual (de)selections.
  const hasInitializedSelection = React.useRef(false);

  React.useEffect(() => {
    hasInitializedSelection.current = false;
  }, [scanAuditLogId]);

  React.useEffect(() => {
    if (hasInitializedSelection.current) return;
    if (!stagingAgents || stagingAgents.length === 0) return;

    const actionableIds = stagingAgents.filter(isActionable).map((a) => a.id);

    setSelectedAgentIds(new Set(actionableIds));
    hasInitializedSelection.current = true;
  }, [stagingAgents]);

  const handleToggleAgent = React.useCallback((agentId) => {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);

      if (next.has(agentId)) {
        next.delete(agentId);
      } else {
        next.add(agentId);
      }

      return next;
    });
  }, []);

  const handleSelectAll = React.useCallback((agentIds) => {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);

      agentIds.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const handleDeselectAll = React.useCallback((agentIds) => {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);

      agentIds.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const handleRemoveFromSkipped = React.useCallback(
    (agent) => {
      setUnIgnoredIds((prev) => {
        const next = new Set(prev);

        next.add(agent.id);

        return next;
      });
      if (!selectedAgentIds.has(agent.id)) {
        setSelectedAgentIds((prev) => {
          const next = new Set(prev);

          next.add(agent.id);

          return next;
        });
      }

      const tabLabel =
        agent.stagingStatus === 'PENDING_UPDATE' ? 'Update' : 'Add';

      setAlert({
        type: 'success',
        message: `${agent.name} added back into the ${tabLabel} list. It will be included in all future scans.`
      });
    },
    [selectedAgentIds, setAlert]
  );

  // Load scanner runs when component mounts or scanner changes
  React.useEffect(() => {
    if (organizationId && scannerId && scanAuditLogId) {
      loadScannerRuns(organizationId, scannerId);
      loadStagingAgents(organizationId, scannerId, scanAuditLogId);
    }
  }, [
    organizationId,
    scannerId,
    scanAuditLogId,
    loadScannerRuns,
    loadStagingAgents
  ]);

  // Navigate back to the list using getPath
  const handleBackToList = React.useCallback(() => {
    history.push(getPath('agentScanners'));
  }, [history]);

  // Navigate back to the scanner detail page
  const handleBackToDetail = React.useCallback(() => {
    history.push(`${getPath('agentScanners')}${scannerId}/`);
  }, [history, scannerId]);

  const assetType =
    scanner.connection?.targetSystem?.assetType?.toLowerCase() || '';
  const assetTypeLabel = FORM_ASSET_TYPE_LABELS[assetType] || 'Agents';

  const handleProceed = React.useCallback(async () => {
    const agents = (stagingAgents || [])
      .filter((agent) => {
        if (isBackendIgnored(agent) && !unIgnoredIds.has(agent.id))
          return false;

        return (
          agent.stagingStatus === 'NEW' ||
          agent.stagingStatus === 'PENDING_UPDATE'
        );
      })
      .map((agent) => ({
        id: agent.id,
        externalId: agent.assetId,
        ignored: selectedAgentIds.has(agent.id) ? 'false' : 'true',
        stagingStatus: agent.stagingStatus
      }));

    setIsPublishing(true);
    setAlert({
      type: 'info',
      message: `Your '${scanner.name}' is currently importing.`
    });

    try {
      await publishAgents(organizationId, scannerId, scanAuditLogId, agents);
      // Success toast is fired by ScannerDetail once polling observes the scan
      // run actually completes — not here, since publishAgents only triggers
      // the async import.
    } catch (error) {
      setAlert({
        type: 'error',
        message:
          error?.status === 409
            ? 'A new scan is ready for review. This current queue is now outdated and cannot be submitted. Review new scan.'
            : `Failed to publish ${assetTypeLabel}. Please try again.`
      });
    } finally {
      setIsPublishing(false);
      // Refresh scanner list so the scanner's state transitions out of
      // PENDING_REVIEW (into IMPORTING) in Redux, which in turn arms the
      // polling effect in ScannerDetail that ultimately fires the success toast.
      if (loadScanners) loadScanners(organizationId);
      loadScannerRuns(organizationId, scannerId);
      history.push(`${getPath('agentScanners')}${scannerId}/`);
    }
  }, [
    selectedAgentIds,
    unIgnoredIds,
    stagingAgents,
    organizationId,
    scannerId,
    scanAuditLogId,
    scanner.name,
    assetTypeLabel,
    setAlert,
    publishAgents,
    loadScanners,
    loadScannerRuns,
    history
  ]);

  const isEffectivelyIgnored = (agent) =>
    isBackendIgnored(agent) && !unIgnoredIds.has(agent.id);

  const selectedCount = stagingAgents
    ? stagingAgents.filter(
        (agent) =>
          selectedAgentIds.has(agent.id) &&
          (agent.stagingStatus === 'NEW' ||
            agent.stagingStatus === 'PENDING_UPDATE') &&
          !isEffectivelyIgnored(agent)
      ).length
    : 0;

  const platformLabel = scanner.connection?.targetSystem?.name || '';

  return (
    <div id="scanAuditLogPage" data-test-id="scanner-audit-log-page">
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.breadcrumbs}>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackToList}
            data-test-id="back-to-list"
          >
            Scanners
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <button
            type="button"
            className={styles.breadcrumbLink}
            onClick={handleBackToDetail}
            data-test-id="back-to-detail"
          >
            {scanner.name}
          </button>
          <span className={styles.breadcrumbSeparator}>/</span>
          <span
            className={styles.breadcrumbCurrent}
            data-test-id="scanner-name-breadcrumb"
          >
            Scan Review
          </span>
        </div>
        <div className={styles.detailHeader}>
          <div className={styles.detailTitleSection}>
            <h1
              className={styles.detailTitle}
              data-test-id="scanner-name-heading"
            >
              Scan Review
              <span display-if={!isFetchingScannerRuns}>
                <span> - </span>
                <FormattedDate
                  testId="scan-config-last-run"
                  date={new Date(scannerRun ? scannerRun.startedAt : '')}
                  format={dateFormat}
                />
              </span>
            </h1>
          </div>
        </div>
      </div>

      <LoadingElement
        message="Loading..."
        data-test-id="loading-element"
        display-if={
          isFetchingScannerRuns || isFetchingStagingAgents || isPublishing
        }
      />

      {!isBannerDismissed && scannerRun?.status === 'FAILED' && (
        <ScanFailureBanner
          title="Scan failed"
          description={
            getScanErrorMessage(scannerRun.summary) ||
            "We couldn't complete the scan. Try again later."
          }
          onDismiss={() => setIsBannerDismissed(true)}
          testId="scan-failure-audit-banner"
        />
      )}

      <div
        className={styles.detailContent}
        display-if={
          !isFetchingScannerRuns && !isFetchingStagingAgents && !isPublishing
        }
      >
        <Overview
          display-if={scanner && scannerRun}
          scannerName={scanner.name}
          connectionName={scanner.connection?.targetSystem?.name || ''}
          scannerRun={scannerRun}
        />
        <Review
          display-if={scanner && stagingAgents}
          isReviewMode={isReviewMode}
          isStale={isStale}
          stagingAgents={stagingAgents}
          region={scanner.connection?.authParameters?.region}
          platformName={scanner.connection?.targetSystem?.name}
          selectedAgentIds={selectedAgentIds}
          unIgnoredIds={unIgnoredIds}
          onToggleAgent={handleToggleAgent}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          onRemoveFromSkipped={handleRemoveFromSkipped}
          assetTypeLabel={assetTypeLabel}
          assetType={assetType}
          platformLabel={platformLabel}
        />
      </div>

      {/* Sticky footer */}
      <div className={styles.pageFooter}>
        <Button
          kind="tertiary"
          onClick={handleBackToDetail}
          data-test-id="go-back-button"
        >
          Go Back
        </Button>
        <Button
          kind="primary"
          onClick={handleProceed}
          disabled={isPublishing}
          data-test-id="proceed-button"
          display-if={isReviewMode}
        >
          {(() => {
            if (isPublishing) return 'Importing...';

            return selectedCount === 0
              ? 'Finalize Review'
              : `Confirm & Import (${selectedCount} ${assetTypeLabel})`;
          })()}
        </Button>
      </div>
    </div>
  );
};

ScanAuditLog.propTypes = {
  scanner: scannerPropType,
  scannerId: PropTypes.string,
  scanAuditLogId: PropTypes.string,
  organizationId: PropTypes.string,
  scannerRun: scannerRunPropType,
  isFetchingScannerRuns: PropTypes.bool,
  isFetchingStagingAgents: PropTypes.bool,
  stagingAgents: PropTypes.array,
  loadScanners: PropTypes.func,
  loadScannerRuns: PropTypes.func,
  loadStagingAgents: PropTypes.func,
  setAlert: PropTypes.func,
  publishAgents: PropTypes.func
};

export default ScanAuditLog;
